import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
    MoreHorizontal, KeyRound, FileText, History, BookOpen,
    Download, Mail, Loader2, AlertTriangle, CheckCircle2, ClipboardCheck,
} from "lucide-react";
import { toast } from "sonner";

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric", month: "short", day: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

async function downloadLoa(downloadUrl, loaNumber) {
    try {
        const resp = await api.get(downloadUrl.replace(/^\/api/, ""), { responseType: "blob" });
        const blob = new Blob([resp.data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${loaNumber || "letter-of-acceptance"}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    } catch {
        toast.error("Could not download LOA.");
    }
}

/**
 * Per-row admin action menu for the Students table.
 *
 *  - (a) Send password reset link
 *  - (b) Send LOA (issues a fresh PDF + emails it as a Resend attachment)
 *  - (c) View LOA history (modal with download links for every LOA ever issued)
 *  - (d) Enrol in another program (delegates to the wizard via onEnrolAgain)
 */
export default function StudentRowActions({ student, onEnrolAgain }) {
    const [busy, setBusy] = useState(null); // "reset" | "loa" | "col" | "welcome" | "history" | null
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyData, setHistoryData] = useState(null);

    const sendPasswordReset = async () => {
        setBusy("reset");
        try {
            await api.post(`/admin/users/${student.id}/send-password-reset`);
            toast.success(`Password reset link emailed to ${student.email}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not send password reset.");
        } finally {
            setBusy(null);
        }
    };

    const sendWelcomeEmail = async () => {
        setBusy("welcome");
        try {
            const { data } = await api.post(`/admin/users/${student.id}/send-welcome-email`);
            toast.success(`Welcome email sent to ${data.sent_to || student.email}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not send welcome email.");
        } finally {
            setBusy(null);
        }
    };

    const sendLoa = async () => {
        setBusy("loa");
        try {
            // Find latest enrolment for this student
            const { data: enrResp } = await api.get(`/admin/users/${student.id}/latest-enrollment`);
            const enr = enrResp?.enrollment;
            if (!enr) {
                toast.error("This student has no enrolments yet — enrol them first.");
                return;
            }
            // Issue + email LOA. If a COL already exists, the new LOA is the
            // "upgrade" — both records stay in the loa_documents collection so
            // the history modal shows the progression.
            const { data } = await api.post(
                `/admin/enrollments/${enr.id}/loa`,
                { email_loa: true },
            );
            const loa = data.loa;
            const wasUpgraded = !!enr.latest_col_id;
            toast.success(
                wasUpgraded
                    ? `Conditional offer upgraded → LOA ${loa.loa_number}${loa.emailed_to ? ` · emailed to ${loa.emailed_to}` : ""}`
                    : loa.emailed_to
                        ? `LOA ${loa.loa_number} emailed to ${loa.emailed_to}`
                        : `LOA ${loa.loa_number} issued.`,
            );
            await downloadLoa(data.download_url, loa.loa_number);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not issue LOA.");
        } finally {
            setBusy(null);
        }
    };

    const sendCol = async () => {
        setBusy("col");
        try {
            const { data: enrResp } = await api.get(`/admin/users/${student.id}/latest-enrollment`);
            const enr = enrResp?.enrollment;
            if (!enr) {
                toast.error("This student has no enrolments yet — enrol them first.");
                return;
            }
            const { data } = await api.post(
                `/admin/enrollments/${enr.id}/col`,
                { email_loa: true },
            );
            const col = data.loa;
            toast.success(
                col.emailed_to
                    ? `Conditional Offer ${col.loa_number} emailed to ${col.emailed_to}`
                    : `Conditional Offer ${col.loa_number} issued.`,
            );
            await downloadLoa(data.download_url, col.loa_number);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not issue Conditional Offer Letter.");
        } finally {
            setBusy(null);
        }
    };

    const openHistory = async () => {
        setBusy("history");
        setHistoryOpen(true);
        try {
            const { data } = await api.get(`/admin/users/${student.id}/loa-history`);
            setHistoryData(data);
        } catch (e) {
            toast.error("Could not load LOA history.");
            setHistoryData({ count: 0, loas: [] });
        } finally {
            setBusy(null);
        }
    };

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10"
                        title="Student actions"
                        data-testid={`student-actions-trigger-${student.id}`}
                    >
                        <MoreHorizontal className="h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="end"
                    className="w-56"
                    data-testid={`student-actions-menu-${student.id}`}
                >
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                        Student actions
                    </DropdownMenuLabel>

                    <DropdownMenuItem
                        onClick={sendPasswordReset}
                        disabled={busy === "reset"}
                        data-testid={`student-action-reset-${student.id}`}
                    >
                        {busy === "reset" ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <KeyRound className="h-4 w-4 mr-2 text-primary" />
                        )}
                        Send password reset link
                    </DropdownMenuItem>

                    <DropdownMenuItem
                        onClick={sendWelcomeEmail}
                        disabled={busy === "welcome"}
                        data-testid={`student-action-send-welcome-${student.id}`}
                    >
                        {busy === "welcome" ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <Mail className="h-4 w-4 mr-2 text-primary" />
                        )}
                        Send welcome email
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />

                    <DropdownMenuItem
                        onClick={sendCol}
                        disabled={busy === "col"}
                        data-testid={`student-action-send-col-${student.id}`}
                    >
                        {busy === "col" ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <ClipboardCheck className="h-4 w-4 mr-2 text-primary" />
                        )}
                        Issue Conditional Offer Letter
                    </DropdownMenuItem>

                    <DropdownMenuItem
                        onClick={sendLoa}
                        disabled={busy === "loa"}
                        data-testid={`student-action-send-loa-${student.id}`}
                    >
                        {busy === "loa" ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <FileText className="h-4 w-4 mr-2 text-amber-700" />
                        )}
                        Send LOA (upgrade COL · issue + email)
                    </DropdownMenuItem>

                    <DropdownMenuItem
                        onClick={openHistory}
                        data-testid={`student-action-history-${student.id}`}
                    >
                        <History className="h-4 w-4 mr-2 text-muted-foreground" />
                        View LOA history
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />

                    <DropdownMenuItem
                        onClick={() => onEnrolAgain?.(student)}
                        data-testid={`student-action-enrol-again-${student.id}`}
                    >
                        <BookOpen className="h-4 w-4 mr-2 text-primary" />
                        Enrol in another program
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
                <DialogContent
                    className="max-w-2xl max-h-[80vh] overflow-y-auto"
                    data-testid={`student-loa-history-dialog-${student.id}`}
                >
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2">
                            <History className="h-5 w-5 text-primary" />
                            LOA history — {student.name || student.email}
                        </DialogTitle>
                        <DialogDescription>
                            Every Letter of Acceptance ever issued for this student. Each
                            record is immutable; if details changed, issue a fresh one instead.
                        </DialogDescription>
                    </DialogHeader>

                    {historyData === null ? (
                        <div className="flex items-center justify-center py-10 text-muted-foreground gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading…
                        </div>
                    ) : historyData.count === 0 ? (
                        <div className="rounded-lg border border-dashed border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
                            <AlertTriangle className="h-6 w-6 mx-auto mb-2 text-amber-500" />
                            No LOAs have been issued for this student yet.
                        </div>
                    ) : (
                        <div className="space-y-2.5">
                            {historyData.loas.map((loa) => (
                                <div
                                    key={loa.id}
                                    className="rounded-md border border-border bg-card p-3 flex items-start gap-3"
                                    data-testid={`loa-history-row-${loa.id}`}
                                >
                                    <FileText className="h-4 w-4 text-amber-700 mt-1" />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <code className="font-mono text-sm font-medium text-foreground">
                                                {loa.loa_number}
                                            </code>
                                            {(loa.document_type || "loa").toLowerCase() === "col" ? (
                                                <Badge
                                                    variant="outline"
                                                    className="text-[10px] border-primary/40 text-primary uppercase tracking-wide"
                                                >
                                                    Conditional Offer
                                                </Badge>
                                            ) : (
                                                <Badge
                                                    variant="outline"
                                                    className="text-[10px] border-amber-500/40 text-amber-700 uppercase tracking-wide"
                                                >
                                                    LOA
                                                </Badge>
                                            )}
                                            <Badge
                                                variant="outline"
                                                className="text-[10px] capitalize"
                                            >
                                                {(loa.payment_mode || "paid_offline").replace(/_/g, " ")}
                                            </Badge>
                                            {loa.emailed_to ? (
                                                <Badge
                                                    variant="outline"
                                                    className="text-[10px] border-primary/40 text-primary"
                                                >
                                                    <CheckCircle2 className="h-2.5 w-2.5 mr-1" />
                                                    Emailed
                                                </Badge>
                                            ) : null}
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-0.5 truncate">
                                            {loa.program_title || "—"} · issued {fmtDate(loa.issued_at)}
                                        </div>
                                        {loa.emailed_to ? (
                                            <div className="text-xs text-muted-foreground mt-0.5 truncate">
                                                <Mail className="h-3 w-3 inline mr-1" />
                                                {loa.emailed_to}
                                                {loa.emailed_at ? ` · ${fmtDate(loa.emailed_at)}` : null}
                                            </div>
                                        ) : null}
                                    </div>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => downloadLoa(loa.download_url, loa.loa_number)}
                                        className="gap-1.5"
                                        data-testid={`loa-history-download-${loa.id}`}
                                    >
                                        <Download className="h-3.5 w-3.5" />
                                        PDF
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
